import {
  ALL_CONFIGS,
  FUNGIBLE_STAKE_CONFIG_SEED,
  STAKE_FUNGIBLE_INFO_SEED,
  FUNGIBLE_VAULT_SEED,
} from '@/config';
import { toPublicKey } from '@/utils/util';
import * as anchor from '@coral-xyz/anchor';
import { BN, Program } from '@coral-xyz/anchor';
import { WalletContextState } from '@solana/wallet-adapter-react';
import { ComputeBudgetProgram, PublicKey, Transaction } from '@solana/web3.js';
import { Fungstake } from './fungstake/fungstake';
import idl from './fungstake/fungstake.json';
import { handleTransaction } from './utils';
import { StakeInfo } from '@/utils/types';

export const stakeProgramId = new PublicKey(idl.address);
export const stakeInterface = idl as Fungstake;

let globalStakeConfig: anchor.IdlAccounts<Fungstake>['stakeConfig'];
let globalStakeConfigPDA: PublicKey;
export class web3FungibleStake {
  constructor() {}

  private async getStakeGlobalConfig(stakeCurrencyMint: PublicKey) {
    if (!globalStakeConfig) {
      const provider = anchor.getProvider();

      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      let [globalStakeConfigPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from(FUNGIBLE_STAKE_CONFIG_SEED), stakeCurrencyMint.toBytes()],
        program.programId,
      );
      const globalStakeConfig = await program.account.stakeConfig.fetch(
        globalStakeConfigPDA,
      );
      return { globalStakeConfig, globalStakeConfigPDA };
    }
    return {
      globalStakeConfigPDA,
      globalStakeConfig,
    };
  }

  async stake(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    amount: number,
    wallet: WalletContextState,
  ) {
    let provider;
    try {
      provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      console.log('stake---agent', {
        stakeCurrencyMint,
        rewardCurrencyMint,
        amount,
        wallet,
      });

      const stakeIx = await program.methods
        .stake(new BN(amount))
        .accounts({
          signer: wallet.publicKey,
          stakeCurrencyMint: toPublicKey(stakeCurrencyMint),
          rewardCurrencyMint: toPublicKey(rewardCurrencyMint),
        })
        .instruction();

      transaction.add(stakeIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed', // FIXME: trick lord confirmed / finalized;
        );

        console.log('Successfully locking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  }

  async unStake(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    amount: number,
    wallet: WalletContextState,
  ) {
    let provider;
    try {
      provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const unStakeIx = await program.methods
        .destake(new BN(amount))
        .accounts({
          signer: wallet.publicKey,
          stakeCurrencyMint: toPublicKey(stakeCurrencyMint),
          rewardCurrencyMint: toPublicKey(rewardCurrencyMint),
        })
        .instruction();

      transaction.add(unStakeIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed', // FIXME: trick lord confirmed / finalized;
        );

        console.log('Successfully unlocking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  }

  async getStakeInfoCache(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
    getNew: boolean,
  ) {
    if (!wallet.publicKey) {
      console.log('User not connected');
      return {
        bump: [],
        hasClaimed: false,
        snapshotAmount: new BN(0),
        stakeAmount: new BN(0),
        staker: wallet.publicKey,
        unstakedAtTime: new BN(0),
        vault: null,
      } as anchor.IdlAccounts<Fungstake>['stakeInfo'];
    }
    // we attempt to cache stake info query since it is unlikely this data will change, unless users stake multiple times.
    // this would help reduce total # of RPC calls
    const stakeInfoLocalStorageKey = `agent-stakeInfo-${wallet.publicKey?.toBase58()}-${stakeCurrencyMint}-${rewardCurrencyMint}}`;
    if (getNew) {
      return await this.getAndStoreStakeInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        wallet,
        stakeInfoLocalStorageKey,
      );
    }
    const stakeInfoJSON = localStorage.getItem(stakeInfoLocalStorageKey);
    if (!stakeInfoJSON) {
      return await this.getAndStoreStakeInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        wallet,
        stakeInfoLocalStorageKey,
      );
    }

    const parsedStakeInfo = JSON.parse(stakeInfoJSON);
    const stakeInfo: StakeInfo = {
      ...parsedStakeInfo,
      snapshotAmount: new BN(parsedStakeInfo.snapshotAmount),
      stakeAmount: new BN(parsedStakeInfo.stakeAmount),
      unstakedAtTime: new BN(parsedStakeInfo.unstakedAtTime),
    };
    return stakeInfo;
  }

  private async getAndStoreStakeInfoToLocalStorage(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
    stakeInfoLocalStorageKey: string,
  ) {
    const stakerInfo = await this.getStakeInfo(
      stakeCurrencyMint,
      rewardCurrencyMint,
      wallet,
    );
    if (!stakerInfo) {
      // fallback default value so it doesn't break the entire page
      return {
        bump: [],
        hasClaimed: false,
        snapshotAmount: new BN(0),
        stakeAmount: new BN(0),
        staker: wallet.publicKey,
        unstakedAtTime: new BN(0),
        vault: null,
      } as anchor.IdlAccounts<Fungstake>['stakeInfo'];
    }
    // localStorage.setItem(
    //   stakeInfoLocalStorageKey,
    //   JSON.stringify({
    //     ...stakerInfo,
    //     snapshotAmount: stakerInfo.snapshotAmount.toString(),
    //     stakeAmount: stakerInfo.stakeAmount.toString(),
    //     unstakedAtTime: stakerInfo.unstakedAtTime.toString(),
    //   }),
    // );
    return stakerInfo;
  }

  private async getStakeInfo(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
  ) {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      let [globalStakeConfigPDA] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(FUNGIBLE_STAKE_CONFIG_SEED),
          toPublicKey(stakeCurrencyMint).toBytes(),
        ],
        program.programId,
      );

      let [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(FUNGIBLE_VAULT_SEED),
          globalStakeConfigPDA.toBytes(),
          new PublicKey(rewardCurrencyMint).toBytes(),
        ],
        program.programId,
      );

      if (!wallet.publicKey) {
        return;
      }

      let [stakerInfoPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(STAKE_FUNGIBLE_INFO_SEED),
          vaultPda.toBytes(),
          wallet.publicKey.toBytes(),
        ],
        program.programId,
      );
      const stakerInfo = await program.account.stakeInfo.fetch(stakerInfoPda);

      return stakerInfo;
    } catch (error) {
      console.log('Error in get stake token transaction', error, error.error);

      return;
    }
  }

  async getVaultInfoCache(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    // won't fetch vault info if we have reached the end of staking vault, and just query from local storage
    const vaultInfoKey = `reset-vaultInfo-${stakeCurrencyMint}-${rewardCurrencyMint}}`;
    const vaultInfoJSON = localStorage.getItem(vaultInfoKey);
    if (!vaultInfoJSON) {
      return await this.getAndStoreVaultInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        vaultInfoKey,
      );
    }
    const parsedVaultInfo = JSON.parse(vaultInfoJSON);
    const vaultInfo: anchor.IdlAccounts<Fungstake>['vault'] = {
      ...parsedVaultInfo,
      totalStaked: new BN(parsedVaultInfo.totalStaked),
      stakeEndTime: new BN(parsedVaultInfo.stakeEndTime),
      totalReward: new BN(parsedVaultInfo.totalReward),
      maxRewardPerWallet: new BN(parsedVaultInfo.maxRewardPerWallet),
    };

    const nowInSecs = Date.now() / 1000;
    // this means the vault has ended -> no need to query vault anymore
    if (
      Number(vaultInfo.stakeEndTime.toString()) > 0 &&
      nowInSecs > Number(vaultInfo.stakeEndTime.toString())
    ) {
      return vaultInfo;
    }
    // else, we update with latest vault info, and cache it into the local storage
    return await this.getAndStoreVaultInfoToLocalStorage(
      stakeCurrencyMint,
      rewardCurrencyMint,
      vaultInfoKey,
    );
  }

  private async getAndStoreVaultInfoToLocalStorage(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    vaultInfoKey: string,
  ) {
    const vaultInfo = await this.getVaultInfo(
      stakeCurrencyMint,
      rewardCurrencyMint,
    );
    const parsedVaultInfo = {
      ...vaultInfo,
      totalStaked: vaultInfo.totalStaked.toString(),
      stakeEndTime: vaultInfo.stakeEndTime.toString(),
      totalReward: vaultInfo.totalReward.toString(),
      maxRewardPerWallet: vaultInfo.maxRewardPerWallet.toString(),
    };

    const nowInSecs = Date.now() / 1000;
    // from party start =>> cached
    // if (nowInSecs > vaultInfo.stakeEndTime.toNumber()) {
    //   localStorage.setItem(vaultInfoKey, JSON.stringify(parsedVaultInfo));
    // }
    return vaultInfo;
  }

  private async getVaultInfo(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    let vaultInfo: anchor.IdlAccounts<Fungstake>['vault'] = {
      totalStaked: new BN('0'),
      stakeEndTime: new BN('0'),
      bump: [],
      maxRewardPerWallet: new BN('0'),
      reachSoftCap: false,
      rewardCurrencyMint: null,
      totalReward: new BN('0'),
      version: 1,
    };
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      let [globalStakeConfigPDA] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(FUNGIBLE_STAKE_CONFIG_SEED),
          toPublicKey(stakeCurrencyMint).toBytes(),
        ],
        program.programId,
      );

      let [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(FUNGIBLE_VAULT_SEED),
          globalStakeConfigPDA.toBytes(),
          new PublicKey(rewardCurrencyMint).toBytes(),
        ],
        program.programId,
      );

      const newVaultInfo = await program.account.vault.fetch(vaultPda);
      return newVaultInfo || vaultInfo;
    } catch (error) {
      console.log('Error in get stake vault info', error, error.error);
      return vaultInfo;
    }
  }

  async getStakeConfig(stakeCurrencyMint: PublicKey) {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }

      const { globalStakeConfig } = await this.getStakeGlobalConfig(
        stakeCurrencyMint,
      );
      return globalStakeConfig;
    } catch (error) {
      console.log('Error in get stake config transaction', error, error.error);

      return;
    }
  }

  async getReward(
    wallet: WalletContextState,
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      const tx = await program.methods
        .queryReward()
        .accounts({
          user: wallet.publicKey,
          stakeCurrencyMint: stakeCurrencyMint,
          rewardCurrencyMint: new PublicKey(rewardCurrencyMint),
        })
        .view();

      return tx;
    } catch (error) {
      console.log('Error in get stake config transaction', error, error.error);

      return 0;
    }
  }

  async getStakeListByUser(wallet: WalletContextState) {
    try {
      const provider = anchor.getProvider();
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      let list = [];

      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }

      const stakeList = await provider.connection.getParsedProgramAccounts(
        program.programId,
        {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 98,
            },
            {
              memcmp: {
                offset: 66, // number of bytes
                bytes: wallet.publicKey.toBase58(), // base58 encoded string
              },
            },
          ],
        },
      );

      for (const item of stakeList) {
        const info = program.coder.accounts.decode<
          anchor.IdlAccounts<Fungstake>['stakeInfo']
        >('stakeInfo', item.account.data as Buffer);
        list = [...list, info];
      }
      return list;
    } catch (error) {
      console.log('Error in get stake list transaction', error, error.error);

      return [];
    }
  }

  async getMapVaultWithToken() {
    try {
      const provider = anchor.getProvider();
      const program = new Program(
        stakeInterface,
        provider,
      ) as Program<Fungstake>;

      let list = [];

      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }

      const vaultList = await provider.connection.getParsedProgramAccounts(
        program.programId,
        {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 75,
            },
          ],
        },
      );

      for (const item of vaultList) {
        const vault = item.pubkey;

        const info = program.coder.accounts.decode<
          anchor.IdlAccounts<Fungstake>['vault']
        >('vault', item.account.data as Buffer);
        list = [...list, { ...info, vault }];
      }
      return list;
    } catch (error) {
      console.log('Error in get stake list transaction', error, error.error);

      return [];
    }
  }
}
